/**
 * The grouped submission pack list (Specs/submission-pack-spec.md, Task B3).
 *
 * This is the body of the Pack page, so it follows the FLAT full-bleed pattern
 * from the design system: no card wrapper, typography carries the group
 * headings, rows are a `divide-y` list.
 *
 * Ordering is the SERVER's. The pack read already sorts by triage group, then
 * `created_at`, then title — including null triage rows, which sort inside the
 * Attachment group. This component therefore only *partitions* the array; it
 * never re-sorts inside a group.
 *
 * Done rows (`ready` = prepared / `not_required`) fold into one summary row at the end of
 * their group, so the list leads with the work still open. A folded row is NOT
 * rendered — anything that scrolls to a `data-row-id` must get its group
 * expanded first. Two props do that: `activeItemId` (the drawer / `?item=`
 * deep link) and `revealItemId` (any other scroll target). A group holding
 * either target renders expanded in the same commit and stays expanded for the
 * session.
 *
 * Filtering happens upstream: the page passes only the matching items and sets
 * `isFiltered`, which hides groups left with nothing to show.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { OwnerPicker } from '@/components/extraction/OwnerPicker'
import { RowActionsMenu } from '@/components/extraction/RowActionsMenu'
import { REMOVED_BY_ADDENDUM_ANCHOR } from '@/components/pack/PackBlockers'
import { PackReadinessPill } from '@/components/pack/PackReadinessPill'
import { formatLodgementDetails } from '@/lib/submission-lodgement'
import { cn } from '@/lib/utils'
import type { PackItem, SubmissionStatus, TriageClass } from '@/lib/api'
import { PACK_PREPARATION_COPY, PACK_STATUS_LABELS } from '@/lib/pack-readiness'

// ===================
// Types
// ===================

interface TeamMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

interface PackListProps {
  bidId: string
  items: PackItem[]
  removedByAddendum?: PackItem[]
  teamMembers?: TeamMember[]
  readOnly?: boolean
  /** The pack deadline — decides when an empty item reads "Missing". */
  deadline?: string | null
  activeItemId?: string | null
  /**
   * A row something outside the list wants scrolled into view. Its group is
   * expanded if the row is folded away.
   */
  revealItemId?: string | null
  /**
   * Folds done rows into one summary row per group. On by default; a status
   * filter turns it off, since a filtered list should show what it matched.
   */
  foldDone?: boolean
  /**
   * Set while a filter narrows `items` (the page filters before passing them
   * in). Groups with no matching rows are then hidden rather than shown as
   * empty headers — an empty header under a filter reads as "none exist".
   */
  isFiltered?: boolean
  /**
   * Opens the add dialog on this group as soon as the list mounts. The empty
   * state's CTA uses it so "Add an item" actually adds an item rather than
   * revealing four empty groups.
   */
  initialAddTo?: TriageClass | null
  /** Task B5 opens the item drawer from here. */
  onRowActivate?: (itemId: string) => void
  /** The link chip (`PackLinkCell`) drops in here. */
  linkSlot?: (item: PackItem) => ReactNode
  onSetStatus: (itemId: string, status: SubmissionStatus | null) => void
  onSetOwner: (itemId: string, ownerId: string | null) => void
  onSetAcknowledged: (itemId: string, acknowledge: boolean) => void
  /**
   * `description` carries the modal's Description textarea. It is part of the
   * seam rather than dropped here: the create endpoint accepts it, and a field
   * the dialog collects but never sends is a silent data loss.
   */
  onAddItem: (title: string, triageClass: TriageClass, description: string | null) => void
  onRemoveItem: (itemId: string) => void
}

// ===================
// Grouping
// ===================

/** Fixed display order. A null triage class folds into Attachments. */
const GROUP_ORDER: TriageClass[] = ['response', 'form', 'attachment', 'acknowledge']

const GROUP_LABELS: Record<TriageClass, string> = {
  response: 'Responses',
  form: 'Forms',
  attachment: 'Attachments',
  acknowledge: 'Acknowledgements',
}

const ADD_LABELS: Record<TriageClass, string> = {
  response: 'Add response',
  form: 'Add form',
  attachment: 'Add attachment',
  acknowledge: 'Add acknowledgement',
}

/** The first column's heading — what the row links to, by group. */
const LINK_COLUMN_LABELS: Record<TriageClass, string> = {
  response: 'Response',
  form: 'File',
  attachment: 'File',
  acknowledge: 'Acknowledged',
}

// ===================
// Layout
// ===================

// A group's heading line and its rows share ONE layout: a title block that
// takes the free width, then a controls group of fixed-width columns. Using the
// same classes for both is what keeps the column labels over their columns at
// every width — when a row's controls wrap under its title, the labels wrap
// under the group name the same way.

const LINE = 'flex flex-wrap items-center gap-x-3 gap-y-2'
const TITLE_BLOCK = 'min-w-[16rem] flex-1'
const CONTROLS = 'flex max-w-full shrink-0 flex-wrap items-center gap-3'
const COLUMN = {
  link: 'w-64 min-w-0 shrink-0',
  status: 'w-44 shrink-0',
  owner: 'w-28 shrink-0',
  menu: 'w-8 shrink-0',
} as const

/** Readiness values that count as done and fold away. */
function isDone(item: PackItem): boolean {
  return item.readiness === 'ready' || item.readiness === 'not_required'
}

function triageOf(item: PackItem): TriageClass {
  return item.triageClass ?? 'attachment'
}

/** "10 prepared · 1 not required", omitting a zero part. */
function foldSummary(doneItems: PackItem[]): string {
  const prepared = doneItems.filter((item) => item.readiness === 'ready').length
  const notRequired = doneItems.length - prepared
  return [
    prepared > 0 ? PACK_PREPARATION_COPY.preparedCount(prepared) : null,
    notRequired > 0 ? `${notRequired} not required` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')
}

/**
 * "1,240 / 1,500 words", or "1,240 words" with no parsed limit. Null until the
 * draft has words — "0 words" under an empty draft is noise, not progress.
 */
function wordCountLine(item: PackItem): string | null {
  const { totalWords, wordLimit } = item.derived
  if (totalWords == null || totalWords <= 0) return null
  const total = totalWords.toLocaleString('en-AU')
  return wordLimit == null
    ? `${total} words`
    : `${total} / ${wordLimit.toLocaleString('en-AU')} words`
}

function groupPackItems(items: PackItem[]): Array<{
  triageClass: TriageClass
  label: string
  items: PackItem[]
}> {
  return GROUP_ORDER.map((triageClass) => ({
    triageClass,
    label: GROUP_LABELS[triageClass],
    items: items.filter((item) => triageOf(item) === triageClass),
  }))
}

const STATUS_CHOICES: Array<{ value: SubmissionStatus; label: string }> = (
  ['ready', 'in_progress', 'not_ready', 'not_required'] as const
).map((value) => ({ value, label: PACK_STATUS_LABELS[value] }))

const ADD_ITEM_FIELDS: ModalFieldConfig[] = [
  { key: 'title', label: 'Item', type: 'text', required: true, fullWidth: true },
  { key: 'description', label: 'Description', type: 'textarea', fullWidth: true },
]

// ===================
// Component
// ===================

export function PackList({
  bidId,
  items,
  removedByAddendum = [],
  teamMembers = [],
  readOnly = false,
  deadline = null,
  activeItemId = null,
  revealItemId = null,
  foldDone = true,
  isFiltered = false,
  initialAddTo = null,
  onRowActivate,
  linkSlot,
  onSetStatus,
  onSetOwner,
  onSetAcknowledged,
  onAddItem,
  onRemoveItem,
}: PackListProps) {
  const [addTo, setAddTo] = useState<TriageClass | null>(initialAddTo)
  const groups = useMemo(() => groupPackItems(items), [items])
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<TriageClass>>(
    () => new Set()
  )

  // Groups that must render expanded because a scroll/drawer target sits in
  // their fold. Derived during render so the row exists in the SAME commit the
  // target is set in — the caller scrolls straight after that commit.
  const targetGroups = useMemo(() => {
    const targets = new Set<TriageClass>()
    for (const id of [activeItemId, revealItemId]) {
      if (!id) continue
      const item = items.find((i) => i.id === id)
      if (item && isDone(item)) targets.add(triageOf(item))
    }
    return targets
  }, [items, activeItemId, revealItemId])

  // ...and kept expanded afterwards, so closing the drawer doesn't fold the
  // row the user just opened back out from under them.
  useEffect(() => {
    if (targetGroups.size === 0) return
    setExpandedGroups((prev) => {
      if ([...targetGroups].every((tc) => prev.has(tc))) return prev
      return new Set([...prev, ...targetGroups])
    })
  }, [targetGroups])

  const expandGroup = (triageClass: TriageClass) =>
    setExpandedGroups((prev) => new Set([...prev, triageClass]))

  const shownGroups = isFiltered ? groups.filter((group) => group.items.length > 0) : groups

  return (
    <div className="space-y-6">
      {removedByAddendum.length > 0 && (
        <RemovedByAddendumStrip bidId={bidId} items={removedByAddendum} />
      )}

      {isFiltered && shownGroups.length === 0 && (
        <p className="text-sm text-muted-foreground">No items match these filters.</p>
      )}

      {shownGroups.map((group) => {
        const isFolded =
          foldDone &&
          !expandedGroups.has(group.triageClass) &&
          !targetGroups.has(group.triageClass)
        const doneItems = isFolded ? group.items.filter(isDone) : []
        const visibleItems =
          doneItems.length > 0 ? group.items.filter((item) => !isDone(item)) : group.items

        return (
          <div key={group.triageClass}>
            <div className={cn(LINE, 'pb-1')} data-group-header={group.triageClass}>
              <div className={cn(TITLE_BLOCK, 'flex items-center gap-2')}>
                <h3 className="text-sm font-medium text-foreground">
                  {group.label}{' '}
                  <span className="text-meta">{group.items.length}</span>
                </h3>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setAddTo(group.triageClass)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {ADD_LABELS[group.triageClass]}
                  </Button>
                )}
              </div>

              {/* Column labels. Plain text rather than table roles — the
                  list is a list. An empty group has no columns to label. The
                  small insets line each label up with the control's own
                  padding inside its column. */}
              {group.items.length > 0 && (
                <div className={cn(CONTROLS, 'text-meta')}>
                  <span className={COLUMN.link}>{LINK_COLUMN_LABELS[group.triageClass]}</span>
                  <span className={cn(COLUMN.status, !readOnly && 'px-1')}>Status</span>
                  <span className={cn(COLUMN.owner, 'px-1.5')}>Owner</span>
                  <span className={COLUMN.menu} aria-hidden="true" />
                </div>
              )}
            </div>

            {/* An empty group is just its header line — the add button keeps it
                reachable without a "nothing here" row per group. */}
            {group.items.length > 0 && (
              <ul
                aria-label={group.label}
                className="divide-y divide-border border-t border-border"
              >
                {visibleItems.map((item) => (
                  <PackRow
                    key={item.id}
                    item={item}
                    teamMembers={teamMembers}
                    readOnly={readOnly}
                    deadline={deadline}
                    isActive={activeItemId === item.id}
                    {...(onRowActivate ? { onRowActivate } : {})}
                    {...(linkSlot ? { linkSlot } : {})}
                    onSetStatus={onSetStatus}
                    onSetOwner={onSetOwner}
                    onSetAcknowledged={onSetAcknowledged}
                    onRemoveItem={onRemoveItem}
                  />
                ))}
                {doneItems.length > 0 && (
                  <li>
                    <button
                      type="button"
                      className="group/fold flex w-full items-center gap-2 py-2.5 text-left text-meta transition-colors hover:text-foreground"
                      onClick={() => expandGroup(group.triageClass)}
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--status-success))]" />
                      <span className="group-hover/fold:underline">
                        {foldSummary(doneItems)} — show
                      </span>
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        )
      })}

      <AddItemModal
        open={addTo !== null}
        onOpenChange={(open) => {
          if (!open) setAddTo(null)
        }}
        title={addTo ? ADD_LABELS[addTo] : 'Add item'}
        description="Track something the tender asks for that Analysis did not pick up."
        fields={ADD_ITEM_FIELDS}
        submitLabel="Add item"
        onSubmit={(values) => {
          const raw = values['title']
          const title = typeof raw === 'string' ? raw.trim() : ''
          if (!addTo || !title) return
          const rawDescription = values['description']
          const description =
            typeof rawDescription === 'string' && rawDescription.trim() !== ''
              ? rawDescription.trim()
              : null
          onAddItem(title, addTo, description)
          setAddTo(null)
        }}
      />
    </div>
  )
}

// ===================
// Row
// ===================

interface PackRowProps {
  item: PackItem
  teamMembers: TeamMember[]
  readOnly: boolean
  deadline: string | null
  isActive: boolean
  onRowActivate?: (itemId: string) => void
  linkSlot?: (item: PackItem) => ReactNode
  onSetStatus: (itemId: string, status: SubmissionStatus | null) => void
  onSetOwner: (itemId: string, ownerId: string | null) => void
  onSetAcknowledged: (itemId: string, acknowledge: boolean) => void
  onRemoveItem: (itemId: string) => void
}

function PackRow({
  item,
  teamMembers,
  readOnly,
  deadline,
  isActive,
  onRowActivate,
  linkSlot,
  onSetStatus,
  onSetOwner,
  onSetAcknowledged,
  onRemoveItem,
}: PackRowProps) {
  const isAcknowledge = item.triageClass === 'acknowledge'
  const canDelete = !readOnly && item.extractionSource === 'manual'
  // `RowActionsMenu` only ever renders source-quote entries and a delete entry.
  // With neither, the kebab opens an empty menu — so don't render the affordance
  // at all (a read-only manual row, or an AI row the extractor gave no quote).
  // Truthiness, not `!== null`: the menu itself gates its quote entries on a
  // truthy quote, so an empty string would leave the kebab just as empty.
  const hasRowActions = Boolean(item.sourceQuote) || canDelete
  const details = formatLodgementDetails(item)
  const words = item.link?.kind === 'artifact' ? wordCountLine(item) : null

  return (
    <li
      data-row-id={item.id}
      className={cn(
        LINE,
        'group py-2.5',
        isActive && 'bg-accent/30'
      )}
    >
      {/* The title keeps a real minimum. When it and the controls don't fit on
          one line, the controls drop to a second line instead of squeezing the
          title to nothing. On one line the title's flex-1 pushes the controls
          right, and their fixed widths keep the columns aligned across rows.
          `max-w-full` + `flex-wrap` let the controls wrap again on the
          narrowest screens rather than overflow sideways. */}
      <div className={TITLE_BLOCK}>
        <button
          type="button"
          className="block w-full truncate text-left text-sm text-foreground hover:underline"
          onClick={() => onRowActivate?.(item.id)}
        >
          {item.title}
        </button>
        {details && (
          <p className="text-meta truncate" title={details}>
            {details}
          </p>
        )}
      </div>

      <div className={CONTROLS}>
        <div className={COLUMN.link}>
          {isAcknowledge ? (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={item.acknowledgedAt !== null}
                disabled={readOnly}
                onCheckedChange={(checked) => onSetAcknowledged(item.id, checked === true)}
                aria-label={`Acknowledge ${item.title}`}
              />
              <span className="text-meta">Acknowledged</span>
            </label>
          ) : (
            <>
              {linkSlot?.(item) ?? <span className="text-meta">—</span>}
              {words && (
                <p
                  className={cn(
                    'truncate',
                    item.derived.overWordLimit ? 'text-xs text-destructive' : 'text-meta'
                  )}
                >
                  {words}
                </p>
              )}
            </>
          )}
        </div>

        <div className={COLUMN.status}>
          {readOnly ? (
            <PackReadinessPill
              readiness={item.readiness}
              triageClass={item.triageClass}
              hasLink={item.link !== null}
              statusSource={item.statusSource}
              deadline={deadline}
            />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Set status for ${item.title}`}
                  className="rounded-lg px-1 py-0.5 hover:bg-accent/50 transition-colors"
                >
                  <PackReadinessPill
                    readiness={item.readiness}
                    triageClass={item.triageClass}
                    hasLink={item.link !== null}
                    statusSource={item.statusSource}
                    deadline={deadline}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {STATUS_CHOICES.map((choice) => (
                  <DropdownMenuItem
                    key={choice.value}
                    onClick={() => onSetStatus(item.id, choice.value)}
                  >
                    {choice.label}
                  </DropdownMenuItem>
                ))}
                {item.statusSource === 'manual' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onSetStatus(item.id, null)}>
                      Use the derived status
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className={COLUMN.owner}>
          <OwnerPicker
            value={item.ownerId}
            teamMembers={teamMembers}
            disabled={readOnly}
            onChange={(userId) => onSetOwner(item.id, userId)}
          />
        </div>

        <div className={COLUMN.menu}>
          {hasRowActions && (
            <RowActionsMenu
              source={item.extractionSource === 'manual' ? 'manual' : 'ai'}
              sourceQuote={item.sourceQuote}
              {...(canDelete ? { onDelete: () => onRemoveItem(item.id) } : {})}
            />
          )}
        </div>
      </div>
    </li>
  )
}

// ===================
// Removed by addendum
// ===================

function RemovedByAddendumStrip({ bidId, items }: { bidId: string; items: PackItem[] }) {
  return (
    <div
      id={REMOVED_BY_ADDENDUM_ANCHOR}
      className="rounded-lg border border-[hsl(var(--status-warning)/0.2)] bg-[hsl(var(--status-warning)/0.08)] p-3"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-[hsl(var(--status-warning))]" />
        <h3 className="text-sm font-medium text-foreground">
          Removed by addendum — review
        </h3>
      </div>
      <p className="text-meta mt-1">
        An addendum withdrew {items.length === 1 ? 'this item' : 'these items'}. Any work
        already linked stays here until you review the change in Analysis.
      </p>
      <ul className="mt-2 divide-y divide-[hsl(var(--status-warning)/0.2)]">
        {items.map((item) => (
          <li
            key={item.id}
            data-row-id={item.id}
            className="flex items-center gap-3 py-1.5 text-sm"
          >
            <span className="min-w-0 flex-1 truncate text-foreground">{item.title}</span>
            <span className="text-meta truncate">
              {item.link
                ? item.link.kind === 'artifact'
                  ? (item.link.sectionTitle ?? item.link.artifactName)
                  : item.link.documentName
                : 'No linked work'}
            </span>
          </li>
        ))}
      </ul>
      <Link
        to={`/app/bids/${bidId}/analysis`}
        className="mt-2 inline-block text-sm text-[hsl(var(--link))] hover:underline"
      >
        Review in Analysis
      </Link>
    </div>
  )
}
